import { Feedback, FeedbackData } from "@madhive/kit-ui";
import { MadFeedbackProps } from "./types";
import { useMadTracker } from "../../hooks";

/**
 * `MadFeedback` is a wrapper around the `Feedback` component from `@madhive/kit-ui` that integrates with `@madhive/mad-tracker`.
 * It allows you to easily collect user feedback and track it with custom events in your analytics.
 * You can pass additional data to be included in the tracking payload, and customize the feedback options.
 */
function MadFeedback({
  data = {},
  domain,
  tags = {
    positive: ["Informative", "Accurate", "Fast"],
    negative: ["Confusing", "Inaccurate", "Slow"]
  },
  ...feedbackProps
}: MadFeedbackProps) {
  const madTracker = useMadTracker();

  function handleFeedback(feedbackData: FeedbackData) {
    if (!feedbackData.sentiment) {
      return;
    }

    feedbackProps.onSubmit?.(feedbackData);
    if (!madTracker) {
      return;
    }

    const sentiment =
      feedbackData.sentiment === "negative" ? "Negative" : "Positive";

    /**
     * Format the data object to have keys in the format of `data${Key}` with the first letter of the original key capitalized,
     * to be included in the tracking payload. We do this for better readability and consistency in the tracking data,
     * making it clear that these fields are additional data from the event.
     */
    const payload = Object.entries(data).reduce<Record<string, string>>(
      (acc, [key, value]) => {
        acc[`data${key.charAt(0).toUpperCase() + key.slice(1)}`] = value;
        return acc;
      },
      {}
    );

    madTracker.track(`${domain} Feedback ${sentiment}`, {
      ...payload,
      dataFeedbackSentiment: feedbackData.sentiment,
      dataFeedbackComment: feedbackData.text || "",
      dataFeedbackTag: feedbackData.tag || ""
    });
  }

  return (
    <Feedback
      {...feedbackProps}
      tags={tags}
      onSubmit={data => {
        handleFeedback(data);
      }}
    />
  );
}

export default MadFeedback;
export * from "./types";
